import React from 'react'
import { PERSONAL_SECTION_TEMPLATES } from './registry'

export function PersonalLandingOrgStyles() {
  return (
    <style>{`
      .org-hero-zoom { animation: orgHeroZoom 18s ease-in-out infinite alternate; }
      .org-float { animation: orgFloat 9s ease-in-out infinite; }
      @keyframes orgHeroZoom {
        from { transform: scale(1); }
        to { transform: scale(1.08); }
      }
      @keyframes orgFloat {
        0%, 100% { transform: translate3d(0, 0, 0); }
        50% { transform: translate3d(0, -18px, 0); }
      }
      @keyframes plNavBackdropIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes plNavSheetIn {
        from { opacity: 0; transform: translateY(-14px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes plNavItemIn {
        from { opacity: 0; transform: translateX(-12px); }
        to { opacity: 1; transform: translateX(0); }
      }
      .pl-nav-mobile-backdrop {
        animation: plNavBackdropIn 0.28s ease-out both;
      }
      .pl-nav-mobile-sheet {
        animation: plNavSheetIn 0.38s cubic-bezier(0.22, 1, 0.36, 1) both;
      }
      .pl-nav-mobile-item {
        animation: plNavItemIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
      }
    `}</style>
  )
}

function sectionTypeLabel(type) {
  const row = PERSONAL_SECTION_TEMPLATES.find((t) => t.type === type)
  return row?.label || type
}

/** 상단 네비·모바일 메뉴 전용 짧은 이름(섹션 편집기 라벨과 별도) */
const PERSONAL_SECTION_NAV_LABEL = {
  'about-profile': '프로필',
  'service-tags': '서비스',
  'proof-stats': '지표',
  'monthly-issue': '이달의 이슈',
  'media-gallery': '사진·자료',
  'image-card': '소개 이미지',
  career: '경력',
  'customer-case': '상담 사례',
  activity: '활동 지역',
  'free-text': '추가 소개',
  contact: '연락처',
  cta: '상담 신청',
  credentials: '자격·이력',
  values: '고객 약속',
  faq: '자주 묻는 질문',
  'ad-review': '심의필·준법',
}

/** 프리셋/기본값으로 채워진 긴 섹션 제목은 네비에 그대로 쓰지 않음 */
const NAV_TITLE_BOILERPLATE = new Set(
  [
    '프로필 소개',
    '제공 서비스',
    '이런 도움을 드립니다',
    '이런 분께 먼저 추천드립니다',
    '도움 드리는 영역',
    '왜 저와 상담하시면 좋을까요',
    '빠르게 정리부터 도와드립니다',
    '제가 상담을 하는 방식',
    '함께해 온 시간',
    '함께해 온 발자취',
    '상담 응대 기준',
    '신뢰를 만드는 지표',
    '상담 후 이렇게 바뀌었어요',
    '실제 상담에서 자주 나온 변화',
    '미디어 갤러리',
    '상담 자료',
    '상담 현장·자료 스케치',
    '현장·세미나·자료 스케치',
    '부담 없이 한 번만 이야기 나눠보세요',
    '상담 문의하기',
    '상담이 필요하시면 편하게 문의해 주세요.',
    '지금 바로 상담 신청하세요',
    '증권만 있어도 괜찮습니다',
    '지금 남기시면 순서대로 연락드립니다',
    '톤이 맞는지 먼저 나눠보세요',
    '자격 및 교육',
    '자격·교육 이력',
    '광고 심의필',
    '광고 심의필 · 준법 표기',
    '상담할 때 지키는 원칙',
    '고객을 향한 약속',
    '자주 묻는 질문',
    '이달의 보험 이슈',
    '이달의 보험 이슈 한눈에',
    '제가 상담할 때 가장 중요하게 보는 것',
    '브랜드 메시지',
    '문의 정보',
    '상담 사례',
    '활동지역',
    '자유 소개',
    '이미지 카드',
    '서비스 태그',
    '성과 지표',
  ].map((s) => s.trim())
)

function navLabelForSection(type, contentTitle) {
  const short = PERSONAL_SECTION_NAV_LABEL[type] || sectionTypeLabel(type)
  const raw = String(contentTitle || '').trim()
  if (!raw) return short
  if (NAV_TITLE_BOILERPLATE.has(raw)) return short
  if (raw.length > 14) return short
  return raw
}

/** 데스크톱 네비 한 줄 유지용(전체 문구는 title로 노출) */
function navLabelForBar(displayLabel, maxChars = 11) {
  const primary = String(displayLabel || '').trim()
  if (primary.length <= maxChars) return primary
  return `${primary.slice(0, Math.max(1, maxChars - 1))}…`
}

export function buildPersonalLandingNavItems(sections) {
  return (sections || [])
    .filter((s) => s && s.enabled !== false && s.type !== 'hero' && s.type !== 'ad-review')
    .map((s) => {
      const content = s.content && typeof s.content === 'object' ? s.content : {}
      const fromContent = String(content.title || '').trim()
      const displayLabel = navLabelForSection(s.type, fromContent)
      const title =
        fromContent && !NAV_TITLE_BOILERPLATE.has(fromContent) ? fromContent : displayLabel
      return {
        id: s.id,
        label: displayLabel,
        labelBar: navLabelForBar(displayLabel),
        title,
        anchorId: `ps-${s.id}`,
      }
    })
}

export function usePersonalLandingScrollNav() {
  const [isScrolled, setIsScrolled] = React.useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)

  React.useEffect(() => {
    const onScroll = () => {
      setIsScrolled((window.scrollY || 0) > 24)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return { isScrolled, mobileMenuOpen, setMobileMenuOpen }
}

export function PersonalLandingOrgNav({
  displayName,
  navItems,
  isScrolled,
  mobileMenuOpen,
  setMobileMenuOpen,
  scrollToTop,
  scrollToAnchor,
}) {
  return (
    <nav
      className="fixed left-0 right-0 top-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: isScrolled ? 'rgba(255,255,255,0.96)' : 'transparent',
        backdropFilter: isScrolled ? 'blur(16px)' : 'none',
        boxShadow: isScrolled ? '0 12px 32px rgba(15, 23, 42, 0.08)' : 'none',
      }}
    >
      <div className="relative z-[70] mx-auto flex h-16 max-w-6xl min-w-0 items-center gap-2 bg-inherit px-4 md:h-20 md:gap-3 md:px-6">
        <button
          type="button"
          onClick={() => {
            scrollToTop()
            setMobileMenuOpen(false)
          }}
          className="min-w-0 shrink text-left"
        >
          <span
            className={`block max-w-[42vw] truncate text-lg font-black md:max-w-[12rem] ${isScrolled ? 'text-slate-900' : 'text-white'}`}
            title={displayName || '소개'}
          >
            {displayName || '소개'}
          </span>
        </button>
        <div className="hidden min-w-0 flex-1 items-center justify-end gap-0.5 overflow-x-auto py-1 [-ms-overflow-style:none] [scrollbar-width:none] md:flex [&::-webkit-scrollbar]:hidden">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => scrollToAnchor(item.anchorId)}
              title={item.title || item.label}
              className={`shrink-0 max-w-[6.5rem] truncate rounded-full px-3 py-2 text-sm font-medium transition lg:max-w-[7.5rem] xl:max-w-[9rem] ${isScrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white/90 hover:bg-white/10'}`}
            >
              {item.labelBar ?? item.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-expanded={mobileMenuOpen}
          aria-controls="pl-nav-mobile-sheet"
          onClick={() => setMobileMenuOpen((v) => !v)}
          className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl md:hidden ${isScrolled ? 'text-slate-900 hover:bg-slate-100' : 'text-white hover:bg-white/10'}`}
        >
          <span className="sr-only">{mobileMenuOpen ? '메뉴 닫기' : '메뉴 열기'}</span>
          <span className="relative block h-4 w-5">
            <span
              className={`absolute left-0 top-1 block h-0.5 w-5 rounded-full bg-current transition-all duration-300 ease-out ${mobileMenuOpen ? 'top-[7px] rotate-45' : ''}`}
            />
            <span
              className={`absolute left-0 top-[7px] block h-0.5 w-5 rounded-full bg-current transition-opacity duration-200 ${mobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}
            />
            <span
              className={`absolute left-0 top-[13px] block h-0.5 w-5 rounded-full bg-current transition-all duration-300 ease-out ${mobileMenuOpen ? 'top-[7px] -rotate-45' : ''}`}
            />
          </span>
        </button>
      </div>
      {mobileMenuOpen ? (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="pl-nav-mobile-backdrop fixed inset-0 z-[60] bg-slate-900/45 backdrop-blur-[2px] md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div
            id="pl-nav-mobile-sheet"
            className="pl-nav-mobile-sheet relative z-[65] border-t border-slate-200/90 bg-white/98 px-4 py-4 shadow-[0_24px_48px_rgba(15,23,42,0.12)] backdrop-blur-md md:hidden"
          >
            <div className="mx-auto max-w-lg space-y-1">
              {navItems.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  style={{ animationDelay: `${55 + i * 48}ms` }}
                  onClick={() => scrollToAnchor(item.anchorId)}
                  title={item.title}
                  className="pl-nav-mobile-item group block w-full rounded-2xl border border-transparent px-4 py-3.5 text-left text-[15px] font-semibold text-slate-800 transition-colors hover:border-slate-200 hover:bg-slate-50 active:scale-[0.99]"
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-slate-300 transition-all group-hover:w-2 group-hover:bg-slate-500" />
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </nav>
  )
}

export function PersonalLandingOrgFooter({ name }) {
  const y = new Date().getFullYear()
  return (
    <footer className="bg-slate-950 px-6 py-10 text-center text-sm text-white/55">
      {name ? `© ${y} ${name}. 설계 EZ` : `© ${y} 설계 EZ`}
    </footer>
  )
}
